import { ITransformedFlowResponse } from '~/types/server.types';
import { IDateQuestionValue } from '../FlowContent/QuestionBlock/MultiInputQuestions/DateQuestion/CXDateQuestion.types';
import { convertToUTCDateTimeStrings } from '~/utils/timezone.utils';
import { dateStringToDateIgnoreTimezone } from '~/utils/date.utils';
import dayjs from '~/common/dayjs';
import { transformFlowResponse } from '~/utils/server-response.utils';
import {
    FlowCXAdapter,
    flowSubmitOptionsDefault,
    IFlowSubmitOptions
} from '~/components/ClientExperience/OneFlow/services/adapters/flow-cx.adapter';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import DOMPurify from 'dompurify';

export class FlowCXService extends FlowCXAdapter {
    async fetchFlow(
        flowId: string,
        timezone: string
    ): Promise<ITransformedFlowResponse> {
        let response;
        if (isViewerMode()) {
            response = await super._fetchViewOnlyFlow(flowId);
        } else {
            response = await super._fetchFlow(flowId);
        }

        return transformFlowResponse(response, timezone);
    }

    // TODO: type data
    async submitFlow(
        flowId: string,
        revisionId: string,
        pageId: string,
        data: any,
        flowSubmitOptions: IFlowSubmitOptions = flowSubmitOptionsDefault
    ) {
        const transformedData = this.transformFlowRequest(data);

        return super.submitFlow(
            flowId,
            revisionId,
            pageId,
            transformedData,
            flowSubmitOptions
        );
    }

    async sign(flowId: string, revisionId: string, pageId: string, data: any) {
        const transformedData = this.transformFlowRequest(data);

        return super.sign(flowId, revisionId, pageId, transformedData);
    }

    async answerQuestion(
        flowId: string,
        revisionId: string,
        pageId: string,
        data: any
    ) {
        const transformedData = this.transformFlowRequest(data);

        return super.answerQuestion(
            flowId,
            revisionId,
            pageId,
            transformedData
        );
    }

    async selectService(
        flowId: string,
        revisionId: string,
        pageId: string,
        data: any
    ) {
        const transformedData = this.transformFlowRequest(data);

        return super.selectService(flowId, revisionId, pageId, transformedData);
    }

    async getFlowPaymentsPreview(
        flowId: string,
        selectedSsiIds: string[],
        selectedCustomQuantities: { block_id: string; quantity: number }[],
        isActiveRevisionMode: boolean = false,
        numberOfPayments?: number
    ) {
        const response = await super._getFlowPaymentsPreview(
            flowId,
            selectedSsiIds,
            selectedCustomQuantities,
            isActiveRevisionMode,
            numberOfPayments
        );

        return {
            variables: response.variables.filter(
                v =>
                    v.type === 'payment_variable' ||
                    v.type === 'contract_field_variable' ||
                    v.type === 'invoice_summary_variable'
            )
        };
    }

    protected transformFlowRequest(data: any) {
        return Object.keys(data).map(key => {
            const preTransformData = data[key];

            return {
                variable_id: key,
                value: this.transformData(preTransformData)
            };
        });
    }

    private transformData(data: any) {
        if (data && data.start_date) {
            return this.convertDateToUTC(data as IDateQuestionValue);
        }
        if (data && typeof data === 'string') {
            return DOMPurify.sanitize(data);
        }
        return data;
    }

    private convertDateToUTC(data: IDateQuestionValue) {
        let start_date, start_time, end_date, end_time;

        if (data.start_date && data.start_time) {
            const converted = convertToUTCDateTimeStrings(
                data.start_date,
                data.start_time,
                'YYYY-MM-DD'
            );
            start_date = converted.date;
            start_time = converted.time;
        } else {
            const converted = dateStringToDateIgnoreTimezone(data.start_date);
            start_date = converted
                ? dayjs(converted).format('YYYY-MM-DD')
                : null;
        }

        if (data.end_date && data.end_time) {
            const converted = convertToUTCDateTimeStrings(
                data.end_date,
                data.end_time,
                'YYYY-MM-DD'
            );
            end_date = converted.date;
            end_time = converted.time;
        } else {
            const converted = dateStringToDateIgnoreTimezone(data.end_date);
            end_date = converted ? dayjs(converted).format('YYYY-MM-DD') : null;
        }

        return {
            start_date,
            start_time,
            end_date,
            end_time
        };
    }

    async selectNumberOfPayments(params: {
        flowId: string;
        numberOfPayments: number;
    }) {
        const res = await this._selectNumberOfPayments(params);

        return transformFlowResponse(res);
    }
}
